import axios from "@/services/middleware/client";
import { useState } from "react";
import { doLogin } from "@/services/service-layer/authService";
import { pushEvent } from "@/services/gtm/gtmService";
import { pricesMapper, SELFSERVICE_PAID_PLANS } from "@/utilities/constants";
import { useLoaderStore } from "@/zustand/loaderStore";
import { useNotificationStore } from "@/zustand/notificationsStore";
import { loadAndInitChargebee } from "./load-chargebee";

const CHARGEBEE_TIMEOUT = 10000;

const usePaymentChargebee = () => {
  const [paymentStep, setPaymentStep] = useState("");
  const { error: showError } = useNotificationStore();

  const ensureChargebeeReady = async (): Promise<boolean> => {
    try {
      await loadAndInitChargebee();
      return (
        !!window.Chargebee &&
        typeof window.Chargebee.getInstance === "function"
      );
    } catch (error) {
      console.error("Failed to load Chargebee:", error);
      return false;
    }
  };

  const withChargebeeTimeout = <T,>(promise: Promise<T>): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(
          () => reject(new Error("Chargebee operation timeout")),
          CHARGEBEE_TIMEOUT
        )
      ),
    ]);
  };

  const openCheckout = async (
    plan: keyof typeof pricesMapper = SELFSERVICE_PAID_PLANS.LITE as keyof typeof pricesMapper,
    currency = "EUR",
    frequency = "monthly",
    numVisitsAddons = 0,
    signupFields: { email?: string; password?: string } = {},
    isGuest = true
  ) => {
    try {
      // Esperar a que Chargebee esté disponible
      const isChargebeeReady = await ensureChargebeeReady();

      if (!isChargebeeReady) {
        showError(
          "Payment system is not available. Please refresh the page and try again.",
          {
            duration: 8000,
          }
        );
        useLoaderStore.getState().setLoading(false);
        return;
      }

      const cbInstance = window.Chargebee.getInstance();

      await withChargebeeTimeout(
        new Promise<void>((resolve, reject) => {
          cbInstance.openCheckout({
            hostedPage: async function () {
              if (isGuest) {
                return axios
                  .post(`/billing/guest-checkout`, {
                    plan: plan,
                    currency: currency,
                    frequency: frequency,
                    numVisitsAddons: numVisitsAddons,
                    signupFields: signupFields,
                  })
                  .then((res) => res.data);
              } else {
                return axios
                  .post(`/billing/checkout`, {
                    plan: plan,
                    currency: currency,
                    frequency: frequency,
                    numVisitsAddons: numVisitsAddons,
                  })
                  .then((res) => res.data);
              }
            },

            loaded: function () {
              // will be called once checkout page is loaded
              resolve();
            },
            error: function (error: any) {
              useLoaderStore.getState().setLoading(false);
              reject(error);
            },
            step: function (step: any) {
              setPaymentStep(step);
              // will be called for each step involved in the checkout process
            },
            success: async function (hostedPageId: string) {
              // will be called when a successful checkout happens.
              useLoaderStore.getState().setLoading(true);
              if (isGuest) {
                pushEvent("opticks_trigger", {
                  value: pricesMapper[plan] || 0.0,
                  currency: currency,
                  plan_name: plan,
                  trigger_type: "signup_plan",
                  transaction_id: Date.now().toString(),
                  items: [
                    {
                      item_name: "signup_plan",
                      item_variant: plan,
                    },
                  ],
                });
                return axios
                  .post(`/billing/guest-checkout-finish`, {
                    hostedPageId: hostedPageId,
                    signupFields: {
                      ...signupFields,
                      hitId: window.optHitId,
                      sid: document.cookie
                        .split("; ")
                        .find((row) => row.startsWith("opticksid="))
                        ?.replace("opticksid=", ""),
                    },
                  })
                  .then((res) => {
                    if (signupFields.email && signupFields.password) {
                      const loginParams = {
                        email: signupFields.email,
                        password: signupFields.password,
                      };
                      setTimeout(() => {
                        doLogin(loginParams);
                      }, 6000);
                    } else {
                      console.error("Email or password is missing");
                    }
                    useLoaderStore.getState().setLoading(false);
                    return res.data;
                  });
              } else {
                return axios
                  .post(`/billing/checkout-finish`, {
                    hostedPageId: hostedPageId,
                  })
                  .then((res) => {
                    pushEvent("opticks_trigger", {
                      value: pricesMapper[plan] || 0.0,
                      currency: currency,
                      plan_name: plan,
                      trigger_type: "paid_plan_post_trial",
                      transaction_id: Date.now().toString(),
                      items: [
                        {
                          item_name: "paid_plan_post_trial",
                          item_variant: plan,
                        },
                      ],
                    });
                    return res;
                  })
                  .then((res) => {
                    setTimeout(() => {
                      window.location.reload();
                      useLoaderStore.getState().setLoading(false);
                    }, 6000);
                    return res.data;
                  });
              }
            },
            close: function () {
              resolve();
            },
          });
        })
      );
    } catch (error) {
      console.error("Chargebee Error:", error);
      useLoaderStore.getState().setLoading(false);
      showError(
        "There was an error processing your payment. Please try again or contact support.",
        {
          duration: 8000,
        }
      );
    }
  };

  const openPortal = async (sect = "", subscription_id = "") => {
    try {
      // Esperar a que Chargebee esté disponible
      const isChargebeeReady = await ensureChargebeeReady();

      if (!isChargebeeReady) {
        showError(
          "Payment portal is not available. Please refresh the page and try again.",
          {
            duration: 8000,
          }
        );
        return;
      }

      const cbInstance = window.Chargebee.getInstance();

      cbInstance.setPortalSession(async function () {
        return axios
          .post(`/billing/portal`, {})
          .then((res) => res.data);
      });

      const cbPortal = cbInstance.createChargebeePortal();

      cbPortal.open(
        {
          loaded() {},
          close() {},
        },
        sect?.length > 0 && subscription_id?.length > 0
          ? {
              sectionType: window.Chargebee.getPortalSections()[sect] || "",
              params: {
                subscriptionId: subscription_id,
              },
            }
          : null
      );
    } catch (error) {
      console.error("Chargebee Portal Error:", error);
      showError(
        "There was an error opening the payment portal. Please try again or contact support.",
        {
          duration: 8000,
        }
      );
    }
  };

  return {
    openCheckout,
    paymentStep,
    openPortal,
  };
};

export default usePaymentChargebee;
